import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocaleStore } from '@/lib/core/stores';
import { mapCmsHeroSlides, parseShopUrlSegments, type HeroSlide } from '@/lib/core/utils';
import type { ApiBlockItemModel } from '@/services/platform/api/model';
import { getCollectionById } from './catalog';

/**
 * Hero slides for a shop screen, with any slide that picks a collection pointed at that collection.
 *
 * The CMS returns picker values as stubs — `{ id, name, route, contentType, properties: {} }` — because
 * the content endpoints never send Umbraco's `expand`. So the two halves of a picked collection resolve
 * differently: the **id** is on the stub and is available on the first render, while the **image** lives
 * in `properties.imageUrl` and needs a round trip through `getCollectionById`.
 *
 * That split is deliberate, not incidental. Building the link from the id synchronously means there is
 * no render where the slide is clickable but still carries the authored href, and no window in which a
 * not-yet-loaded value stringifies into a real, clickable `/collection_undefined`.
 *
 * The link is rebuilt rather than patched: a slide authored as an external `_blank` link has to
 * navigate in-app once it points at a collection, so `target`/`newTab` are dropped on purpose.
 *
 * The image only ever overrides — a collection with no `imageUrl`, or one still in flight, leaves the
 * authored media in place so the hero never goes blank.
 */
export function useShopHeroSlides(items?: ApiBlockItemModel[]): HeroSlide[] {
  const locale = useLocaleStore(s => s.locale);
  const { pathname } = useLocation();
  // First three segments of the current URL, e.g. `/en/shop/women` — the same base the discover
  // banner and the section CTAs build their `collection_` links from.
  const { sectionPath } = parseShopUrlSegments(pathname);
  const [images, setImages] = useState<Record<string, string>>({});

  const slides = mapCmsHeroSlides(items);
  const ids = [...new Set(slides.map(s => s.collectionId).filter(Boolean) as string[])];
  const idsKey = ids.join(',');

  useEffect(() => {
    if (ids.length === 0) {
      setImages({});
      return;
    }
    let cancelled = false;
    Promise.all(ids.map(id => getCollectionById(id, locale)))
      .then(results => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        results.forEach((collection, i) => {
          const imageUrl = collection?.properties?.imageUrl;
          if (imageUrl) next[ids[i]] = imageUrl;
        });
        setImages(next);
      })
      .catch(() => {
        if (!cancelled) setImages({});
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey, locale]);

  return slides.map(slide => {
    const collectionId = slide.collectionId;
    if (!collectionId) return slide;
    return {
      ...slide,
      image: images[collectionId] ?? slide.image,
      link: {
        href: `${sectionPath}/discover/collection_${collectionId}`,
        label: slide.link?.label ?? '',
      },
    };
  });
}
